import { createSignal, createResource, For, Show } from "solid-js";
import { useAdminContext } from "./AdminContext";
import socket from "./socket";
import { useI18n } from "./i18n";

export default function AdminYears() {
  const t = useI18n();
  const [adminStore, setAdminStore] = useAdminContext();
  const [currentYear, setCurrentYear] = createSignal(new Date().getFullYear());
  const [selectedYear, setSelectedYear] = createSignal(null);
  const [yearConventions, setYearConventions] = createSignal([]);
  const [saving, setSaving] = createSignal(false);
  const [message, setMessage] = createSignal(null);
  const [loading, setLoading] = createSignal(false);

  // Available years for the dropdown (current year and 10 years back)
  const availableYears = Array.from({ length: 10 }, (_, i) => 
    new Date().getFullYear() - i
  );

  // Fetch current year setting when component loads
  const [yearConfig] = createResource(async () => {
    return new Promise((resolve) => {
      socket.emit("admin-get-current-year", (data) => {
        if (data && data.value) {
          setCurrentYear(parseInt(data.value));
        }
        resolve(data);
      });
    });
  });

  // Handle saving the new current year
  const saveCurrentYear = () => {
    // Show a confirmation dialog explaining the implications
    if (!confirm(t("WARNING: Changing the academic year will reset ALL student promo assignments. Students will need to select their promo again. This action cannot be undone. Are you sure you want to proceed?"))) {
      return;
    }
    
    setSaving(true);
    setMessage(null);
    
    socket.emit("admin-set-current-year", currentYear(), (response) => {
      setSaving(false);
      if (response.success) {
        const studentsReset = response.studentsReset || 0;
        setMessage({ 
          type: "success", 
          text: `${t("Academic year updated successfully.")} ${studentsReset} ${studentsReset !== 1 ? t("students") : t("student")} ${t("had their promo reset")}.` 
        });
      } else {
        setMessage({ type: "error", text: t("Failed to update academic year") });
      }
      setTimeout(() => setMessage(null), 5000);
    });
  };

  // Fetch conventions for a specific year
  const fetchYearConventions = (year) => {
    if (!year) return;
    
    setLoading(true);
    setYearConventions([]);
    
    socket.emit("admin-get-year-conventions", year, (data) => {
      setLoading(false);
      if (data && data.promos) {
        setYearConventions(data.promos);
      }
    });
  };

  return (
    <div class="container mt-4">
      <h1 class="mb-4">{t("Academic Year Management")}</h1>
      
      <div class="row">
        <div class="col-md-6">
          <div class="card mb-4 border-warning">
            <div class="card-header bg-warning text-dark">
              <h5 class="mb-0">{t("Current Academic Year")}</h5>
            </div>
            <div class="card-body">
              <div class="alert alert-warning">
                <strong>{t("Important:")}</strong> {t("Changing the academic year will reset all student promo assignments. Students will need to select their promo again when they login.")}
              </div>
              
              <Show when={yearConfig.loading}>
                <p>{t("Loading current year setting...")}</p>
              </Show>
              
              <Show when={!yearConfig.loading}>
                <form onSubmit={(e) => { e.preventDefault(); saveCurrentYear(); }}>
                  <div class="mb-3">
                    <label for="currentYearSelect" class="form-label fw-bold">{t("Set Current Academic Year")}</label>
                    <select 
                      id="currentYearSelect"
                      class="form-select"
                      value={currentYear()}
                      onChange={(e) => setCurrentYear(parseInt(e.target.value))}
                    >
                      <For each={availableYears}>
                        {(year) => (
                          <option value={year}>{year}</option>
                        )}
                      </For>
                    </select>
                    <div class="form-text">
                      {t("This setting determines the academic year for new conventions.")}
                    </div>
                  </div>
                  
                  <button 
                    type="submit" 
                    class="btn btn-warning"
                    disabled={saving()}
                  >
                    {saving() ? t("Saving...") : t("Update Academic Year")}
                  </button>
                  
                  <Show when={message()}>
                    <div class={`alert alert-${message().type === "success" ? "success" : "danger"} mt-3`}>
                      {message().text}
                    </div>
                  </Show>
                </form>
              </Show>
            </div>
          </div>
        </div>
        
        <div class="col-md-6">
          <div class="card mb-4">
            <div class="card-header">
              <h5 class="mb-0">{t("View Conventions by Year")}</h5>
            </div>
            <div class="card-body">
              <div class="mb-3">
                <label for="yearSelect" class="form-label">{t("Select Year")}</label>
                <select 
                  id="yearSelect"
                  class="form-select"
                  value={selectedYear() || ""}
                  onChange={(e) => {
                    const year = parseInt(e.target.value);
                    setSelectedYear(year);
                    fetchYearConventions(year); // Automatically fetch when year changes
                  }}
                >
                  <option value="">{t("Choose a year...")}</option>
                  <For each={availableYears}>
                    {(year) => (
                      <option value={year}>{year}</option>
                    )}
                  </For>
                </select>
                <div class="form-text">
                  {t("Select a year to view pending and validated conventions.")}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <Show when={selectedYear()}>
        <div class="card mt-4">
          <div class="card-header">
            <h5 class="mb-0">{t("Conventions for")} {selectedYear()}</h5>
          </div>
          <div class="card-body">
            <Show when={loading()}>
              <div class="text-center">
                <div class="spinner-border" role="status"></div>
                <p>{t("Loading conventions...")}</p>
              </div>
            </Show>
            
            <Show when={!loading() && yearConventions().length === 0}>
              <p class="text-center">{t("No pending or validated conventions found for")} {selectedYear()}.</p>
            </Show>
            
            <Show when={!loading() && yearConventions().length > 0}>
              <For each={yearConventions()}>
                {(promo) => (
                  <div class="mb-4">
                    <h5 class="border-bottom pb-2">{promo.name}</h5>
                    
                    <Show when={promo.conventions.length > 0} fallback={<p>{t("No conventions in this class.")}</p>}>
                      <div class="table-responsive">
                        <table class="table table-hover">
                          <thead>
                            <tr>
                              <th>{t("Student")}</th>
                              <th>{t("Business")}</th>
                              <th>{t("Period")}</th>
                              <th>{t("Status")}</th>
                            </tr>
                          </thead>
                          <tbody>
                            <For each={promo.conventions}>
                              {(convention) => (
                                <tr>
                                  <td>
                                    {convention.fullname ? (
                                      <span>{convention.fullname} <small class="text-muted">({convention.username})</small></span>
                                    ) : (
                                      convention.username
                                    )}
                                  </td>
                                  <td>{convention.info?.business || t("Not specified")}</td>
                                  <td>
                                    {convention.info?.startDate || t("Not specified")} - {convention.info?.endDate || t("Not specified")}
                                  </td>
                                  <td>
                                    <span class={`badge ${
                                      convention.status === 'validated' ? 'bg-success' :
                                      convention.status === 'pending' ? 'bg-warning' :
                                      convention.status === 'active' ? 'bg-primary' : 
                                      'bg-secondary'
                                    }`}>
                                      {t(convention.status)}
                                    </span>
                                  </td>
                                </tr>
                              )}
                            </For>
                          </tbody>
                        </table>
                      </div>
                    </Show>
                  </div>
                )}
              </For>
            </Show>
          </div>
        </div>
      </Show>
    </div>
  );
}
